import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, GripVertical, Edit3, ChevronDown, ChevronUp } from 'lucide-react';
import { ProductContentSection, ContentTemplate } from '@/types/modernProductContent';
import { motion, AnimatePresence } from 'framer-motion';
import { SectionContentEditor } from './SectionContentEditor';

interface ModernProductContentManagerProps {
  productId: string;
  sections: ProductContentSection[];
  onSectionsChange: (sections: ProductContentSection[]) => void;
}

export const ModernProductContentManager: React.FC<ModernProductContentManagerProps> = ({
  productId,
  sections,
  onSectionsChange
}) => {
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [newSection, setNewSection] = useState<Partial<ProductContentSection>>({
    section_type: 'specs',
    title: '',
    subtitle: '',
    content: {},
    layout_config: {},
    display_order: sections.length,
    is_active: true
  });

  const toggleSectionExpansion = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const sectionTypes = [
    { value: 'description', label: 'Product Description', icon: '📝' },
    { value: 'hero', label: 'Product Overview', icon: '🏆' },
    { value: 'specs', label: 'Specifications', icon: '📋' },
    { value: 'features', label: 'Features', icon: '⭐' },
    { value: 'story', label: 'Product Story', icon: '📖' },
    { value: 'gallery', label: 'Media Gallery', icon: '🖼️' },
    { value: 'guarantees', label: 'Warranties & Trust', icon: '🛡️' },
    { value: 'technical', label: 'Technical Details', icon: '🔧' }
  ];

  const addSection = () => {
    const section: ProductContentSection = {
      id: `temp_${Date.now()}`,
      product_id: productId,
      section_type: newSection.section_type as any,
      title: newSection.title || 'Untitled Section',
      subtitle: newSection.subtitle,
      content: getDefaultContent(newSection.section_type as any),
      layout_config: getDefaultLayoutConfig(newSection.section_type as any),
      display_order: sections.length,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    onSectionsChange([...sections, section]);
    setIsAddingSection(false);
    setNewSection({
      section_type: 'specs',
      title: '',
      subtitle: '',
      content: {},
      layout_config: {},
      display_order: sections.length + 1,
      is_active: true
    });
  };

  const getDefaultContent = (sectionType: string) => {
    switch (sectionType) {
      case 'description':
        return {
          description: 'Enter your product description here...'
        };
      case 'specs':
        return {
          specs: [
            { key: 'Brand', value: '', featured: true, copyable: true },
            { key: 'Model', value: '', featured: true, copyable: true },
            { key: 'Weight', value: '', unit: 'g', copyable: true }
          ]
        };
      case 'features':
        return {
          features: [
            { title: 'Premium Quality', description: 'Built with premium materials', type: 'highlight', featured: true },
            { title: 'Fast Performance', description: 'Optimized for speed', type: 'benefit' }
          ]
        };
      case 'gallery':
        return {
          gallery_images: [],
          show_captions: true,
          enable_zoom: true
        };
      case 'hero':
        return {
          description: 'Product overview and key selling points',
          highlights: []
        };
      default:
        return {};
    }
  };

  const getDefaultLayoutConfig = (sectionType: string) => {
    switch (sectionType) {
      case 'specs':
        return { style: 'grid', columns: 2, searchable: true };
      case 'features':
        return { style: 'cards', animation: 'fadeIn' };
      case 'hero':
        return { style: 'modern', background: 'gradient' };
      default:
        return { style: 'default' };
    }
  };

  const updateSection = (sectionId: string, updates: Partial<ProductContentSection>) => {
    const updatedSections = sections.map(section =>
      section.id === sectionId
        ? { ...section, ...updates, updated_at: new Date().toISOString() }
        : section
    );
    onSectionsChange(updatedSections);
  };

  const deleteSection = (sectionId: string) => {
    const filteredSections = sections.filter(s => s.id !== sectionId);
    onSectionsChange(filteredSections);
  };

  const moveSection = (sectionId: string, direction: 'up' | 'down') => {
    const currentIndex = sections.findIndex(s => s.id === sectionId);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === sections.length - 1)
    ) {
      return;
    }

    const newSections = [...sections];
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    [newSections[currentIndex], newSections[targetIndex]] = [newSections[targetIndex], newSections[currentIndex]];
    
    // Update display orders
    newSections.forEach((section, index) => {
      section.display_order = index;
    });

    onSectionsChange(newSections);
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-foreground">Product Content Manager</h3>
            <p className="text-muted-foreground">Create engaging product sections with modern layouts</p>
          </div>
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsAddingSection(true);
            }} 
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Section
          </Button>
        </div>

        {sections.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📋</div>
            <h4 className="text-xl font-semibold text-foreground mb-2">No content sections yet</h4>
            <p className="text-muted-foreground mb-6">Start building engaging product content with our modern sections</p>
            <Button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsAddingSection(true);
              }} 
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Section
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {sections
              .sort((a, b) => a.display_order - b.display_order)
              .map((section) => (
                <motion.div
                  key={section.id}
                  layout
                  className="border rounded-lg p-4 bg-card"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">
                          {sectionTypes.find(t => t.value === section.section_type)?.icon} {section.title}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {sectionTypes.find(t => t.value === section.section_type)?.label}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveSection(section.id, 'up')}
                        disabled={sections.findIndex(s => s.id === section.id) === 0}
                      >
                        ↑
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveSection(section.id, 'down')}
                        disabled={sections.findIndex(s => s.id === section.id) === sections.length - 1}
                      >
                        ↓
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => toggleSectionExpansion(section.id)}
                      >
                        {expandedSections.has(section.id) ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteSection(section.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-2 mb-4">
                    <div>
                      <Label htmlFor={`title-${section.id}`}>Title</Label>
                      <Input
                        id={`title-${section.id}`}
                        value={section.title}
                        onChange={(e) => updateSection(section.id, { title: e.target.value })}
                        placeholder="Section title"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`subtitle-${section.id}`}>Subtitle</Label>
                      <Input
                        id={`subtitle-${section.id}`}
                        value={section.subtitle || ''}
                        onChange={(e) => updateSection(section.id, { subtitle: e.target.value })}
                        placeholder="Section subtitle (optional)"
                      />
                    </div>
                  </div>

                  <AnimatePresence>
                    {expandedSections.has(section.id) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-t pt-4"
                      >
                        <SectionContentEditor
                          section={section}
                          onUpdate={(updatedSection) => updateSection(section.id, updatedSection)}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
          </div>
        )}

        {isAddingSection && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-6 border rounded-lg bg-muted/50"
          >
            <h4 className="text-lg font-semibold text-foreground mb-4">Add New Section</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="section-type">Section Type</Label>
                <Select
                  value={newSection.section_type}
                  onValueChange={(value) => setNewSection({ ...newSection, section_type: value as any })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose section type" />
                  </SelectTrigger>
                  <SelectContent>
                    {sectionTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="section-title">Title</Label>
                <Input
                  id="section-title"
                  value={newSection.title}
                  onChange={(e) => setNewSection({ ...newSection, title: e.target.value })}
                  placeholder="Section title"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="section-subtitle">Subtitle</Label>
                <Input
                  id="section-subtitle"
                  value={newSection.subtitle}
                  onChange={(e) => setNewSection({ ...newSection, subtitle: e.target.value })}
                  placeholder="Section subtitle (optional)"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsAddingSection(false);
                }}
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addSection();
                }} 
                disabled={!newSection.title}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Section
              </Button>
            </div>
          </motion.div>
        )}
      </Card>
    </div>
  );
};